'use client';

import { VoteType } from '@prisma/client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CommentData } from '../../../types/shared';
import { useTrafficItemEvents } from './TrafficGlobalEvents';

/** ✅ Разделяем: votes отдельно от comments */
type RealtimeVotesContextType = {
  likes: number;
  dislikes: number;
  myVote: VoteType | null;
  optimisticVoteUpdate: (voteType: VoteType) => void;
};

type RealtimeCommentsContextType = {
  comments: CommentData[];
  addOptimisticComment: (comment: CommentData) => void;
  deleteOptimisticComment: (commentId: string) => void;
};

const RealtimeVotesContext = createContext<RealtimeVotesContextType | undefined>(undefined);
const RealtimeCommentsContext = createContext<RealtimeCommentsContextType | undefined>(undefined);

export function RealtimeProvider({
  children,
  reportId,
  initialData,
}: {
  children: ReactNode;
  reportId: string;
  initialData: {
    likes: number;
    dislikes: number;
    myVote: VoteType | null;
    comments?: CommentData[]; // оставим, чтобы не ломать текущий код, но…
  };
}) {
  const [likes, setLikes] = useState(initialData.likes);
  const [dislikes, setDislikes] = useState(initialData.dislikes);
  const [myVote, setMyVote] = useState<VoteType | null>(initialData.myVote);

  /** ✅ comments отдельным стейтом */
  const [comments, setComments] = useState<CommentData[]>(() => initialData.comments ?? []);

  /** ✅ Синхронизация только votes (это ок) */
  useEffect(() => {
    setLikes(initialData.likes);
    setDislikes(initialData.dislikes);
    setMyVote(initialData.myVote);
    // ⚠️ comments специально НЕ синкаем тут постоянно — иначе будут сбросы
  }, [initialData.likes, initialData.dislikes, initialData.myVote]);

  /** ✅ Одноразовая подхватка comments (твой старый “костыль”, но аккуратнее) */
  useEffect(() => {
    const incoming = initialData.comments ?? [];
    if (incoming.length > 0 && comments.length === 0) {
      setComments(incoming); // ✅ всегда массив
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData.comments]);

  const event = useTrafficItemEvents(reportId);

  useEffect(() => {
    if (!event) return;

    if (event.type === 'VOTE_UPDATE') {
      setLikes(event.payload.likes);
      setDislikes(event.payload.dislikes);
      return;
    }

    if (event.type === 'NEW_COMMENT') {
      const p = event.payload;
      setComments((prev) => {
        if (prev.some((c) => c.id === p.id)) return prev;
        return [
          ...prev,
          { id: p.id, text: p.text, createdAt: p.createdAt, parentId: p.parentId, user: p.user },
        ];
      });
      return;
    }

    if (event.type === 'DELETE_COMMENT') {
      setComments((prev) => prev.filter((c) => c.id !== event.payload.commentId));
    }
  }, [event]);

  const addOptimisticComment = useCallback((c: CommentData) => {
    setComments((p) => [...p, c]);
  }, []);

  const deleteOptimisticComment = useCallback((id: string) => {
    setComments((p) => p.filter((c) => c.id !== id));
  }, []);

  const optimisticVoteUpdate = useCallback((newVoteType: VoteType) => {
    setMyVote((currentMyVote) => {
      if (currentMyVote === newVoteType) {
        if (newVoteType === 'LIKE') setLikes((l) => Math.max(0, l - 1));
        else setDislikes((d) => Math.max(0, d - 1));
        return null;
      }

      if (currentMyVote === 'LIKE') setLikes((l) => Math.max(0, l - 1));
      if (currentMyVote === 'DISLIKE') setDislikes((d) => Math.max(0, d - 1));

      if (newVoteType === 'LIKE') setLikes((l) => l + 1);
      else setDislikes((d) => d + 1);

      return newVoteType;
    });
  }, []);

  /** ✅ Ключевое: value votes НЕ зависит от comments */
  const votesValue = useMemo(
    () => ({ likes, dislikes, myVote, optimisticVoteUpdate }),
    [likes, dislikes, myVote, optimisticVoteUpdate],
  );

  /** ✅ А commentsValue НЕ зависит от likes/dislikes/myVote */
  const commentsValue = useMemo(
    () => ({ comments, addOptimisticComment, deleteOptimisticComment }),
    [comments, addOptimisticComment, deleteOptimisticComment],
  );

  return (
    <RealtimeVotesContext.Provider value={votesValue}>
      <RealtimeCommentsContext.Provider value={commentsValue}>
        {children}
      </RealtimeCommentsContext.Provider>
    </RealtimeVotesContext.Provider>
  );
}

/** ✅ Новый хук: только голоса */
export function useRealtimeVotes() {
  const ctx = useContext(RealtimeVotesContext);
  if (!ctx) throw new Error('useRealtimeVotes must be used within RealtimeProvider');
  return ctx;
}

/** ✅ Новый хук: только комментарии */
export function useRealtimeComments() {
  const ctx = useContext(RealtimeCommentsContext);
  if (!ctx) throw new Error('useRealtimeComments must be used within RealtimeProvider');
  return ctx;
}

/**
 * ⚠️ Оставим старый хук, если где-то ещё используется.
 * Но НЕ используй его в местах, где важна производительность.
 */
export function useRealtime() {
  const v = useRealtimeVotes();
  const c = useRealtimeComments();
  return { ...v, ...c };
}
